/**
 * PlanesPage — información del plan mensual + paquetes de créditos + "pago"
 * (T-087). El paywall es FICTICIO a propósito (prueba controlada): no hay
 * pasarela; el pago es manual y el administrador activa el plan/créditos desde
 * el panel admin. Esta página informa, muestra el estado real del usuario
 * (GET /me/credits) y da las instrucciones de pago.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../lib/axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, KeyRound, Coins, CheckCircle2, Clock, Sparkles } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';

const money = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

const PlanesPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    axiosInstance.get('/me/credits')
      .then((r) => setData(r.data))
      .catch(() => setData({}));
  }, []);

  const sub = data?.subscription;
  const packages = data?.packages || [];

  return (
    <div className="min-h-screen app-gradient">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate(-1)} className="hover:bg-white/50 dark:hover:bg-slate-700/50">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Planes y créditos
            </h1>
          </div>
          <ThemeToggle className="glass border-0" />
        </div>

        {/* Plan mensual */}
        <Card className="glass border-0 mb-6">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <KeyRound className="w-5 h-5 text-violet-600" />
                  <h2 className="text-xl font-semibold">Plan mensual de plataforma</h2>
                </div>
                <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-1.5">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> Usa tu PROPIA llave de OpenRouter (BYOK): pagas tus tokens directo al proveedor, sin consumir créditos.</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> Elige tus modelos de chat, imagen y visión.</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> Cubre la cuota de infraestructura de la plataforma.</li>
                </ul>
              </div>
              <div className="text-right">
                {sub?.active ? (
                  <div className="px-3 py-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-sm font-medium">
                    ✓ Activo hasta {new Date(sub.until).toLocaleDateString('es-MX')}
                  </div>
                ) : (
                  <div className="px-3 py-2 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-sm font-medium flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Sin plan activo
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Paquetes de créditos */}
        <Card className="glass border-0 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <Coins className="w-5 h-5 text-amber-500" />
              <h2 className="text-xl font-semibold">Paquetes de créditos</h2>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
              Los créditos pagan el uso de IA con la llave de la plataforma (sin BYOK).
              Precios en MXN antes de IVA. Tu saldo actual: <b>{data?.credits_balance ?? '…'}</b> créditos.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {packages.map((pkg) => (
                <div key={pkg.id} className="rounded-xl border border-white/30 bg-white/40 dark:bg-slate-800/40 p-4 text-center">
                  <div className="font-semibold mb-1 flex items-center justify-center gap-1">
                    <Sparkles className="w-4 h-4 text-sky-500" />{pkg.name}
                  </div>
                  <div className="text-2xl font-bold">{pkg.credits}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">créditos</div>
                  <div className="text-lg font-semibold text-sky-600 dark:text-sky-400">{money(pkg.price_mxn)}</div>
                  <div className="text-[10px] text-slate-400">+ IVA</div>
                </div>
              ))}
              {packages.length === 0 && (
                <p className="text-sm text-slate-500 col-span-3">Cargando paquetes…</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pago (manual — prueba controlada) */}
        <Card className="glass border-0">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-2">¿Cómo pagar?</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
              Durante la prueba controlada el pago es <b>manual</b> (aún no hay pasarela en línea):
            </p>
            <ol className="text-sm text-slate-600 dark:text-slate-300 space-y-2 list-decimal list-inside mb-4">
              <li>Realiza tu pago por transferencia a la cuenta indicada por el administrador.</li>
              <li>Envía tu comprobante con el correo de tu cuenta a <b>[CORREO DE CONTACTO]</b>.</li>
              <li>El administrador activa tu plan o abona tus créditos — normalmente el mismo día.</li>
            </ol>
            <p className="text-xs text-slate-400">
              Los créditos no son reembolsables ni transferibles salvo lo que exija la ley.
              Consulta los <a href="/legal/terms" className="underline hover:text-sky-500">Términos y Condiciones</a>.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PlanesPage;
